/**
 * Frontend Logger
 * Centralized logging utility for frontend errors and events
 */

export const LogLevel = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug'
} as const;

export type LogLevel = typeof LogLevel[keyof typeof LogLevel];

export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  timestamp: string;
  stack?: string;
  userAgent?: string;
  url?: string;
}

class Logger {
  private isProduction: boolean;
  private apiUrl: string;

  constructor() {
    this.isProduction = import.meta.env.PROD;
    this.apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  }

  private createLogEntry(level: LogLevel, message: string, context?: Record<string, unknown>, error?: Error): LogEntry {
    return {
      level,
      message,
      context: {
        ...context,
        env: this.isProduction ? 'production' : 'development',
      },
      timestamp: new Date().toISOString(),
      stack: error?.stack,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };
  }

  private logToConsole(entry: LogEntry): void {
    const style = entry.level === LogLevel.ERROR ? 'color: red; font-weight: bold' : 'color: gray';
    const prefix = `[${entry.timestamp}] ${entry.level.toUpperCase()}:`;

    console.groupCollapsed(`%c${prefix} ${entry.message}`, style);
    if (entry.context) {
      console.log('Context:', entry.context);
    }
    if (entry.stack) {
      console.log('Stack:', entry.stack);
    }
    console.log('URL:', entry.url);
    console.groupEnd();
  }

  private async sendToBackend(entry: LogEntry): Promise<void> {
    if (!this.isProduction) return; // Only send to backend in production

    try {
      await fetch(`${this.apiUrl}/api/logs/client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(entry),
      }).catch(() => {
        // Silently fail - don't create infinite loop if logging endpoint is down
      });
    } catch {
      // Silently fail
    }
  }

  error(message: string, context?: Record<string, unknown>, error?: Error): void {
    const entry = this.createLogEntry(LogLevel.ERROR, message, context, error);
    this.logToConsole(entry);
    this.sendToBackend(entry);
  }

  warn(message: string, context?: Record<string, unknown>): void {
    const entry = this.createLogEntry(LogLevel.WARN, message, context);
    this.logToConsole(entry);
    this.sendToBackend(entry);
  }

  info(message: string, context?: Record<string, unknown>): void {
    const entry = this.createLogEntry(LogLevel.INFO, message, context);

    // Only log info in development
    if (!this.isProduction) {
      this.logToConsole(entry);
    }
  }

  debug(message: string, context?: Record<string, unknown>): void {
    const entry = this.createLogEntry(LogLevel.DEBUG, message, context);

    // Only log debug in development
    if (!this.isProduction) {
      this.logToConsole(entry);
    }
  }
}

// Singleton instance
export const logger = new Logger();

// Export helper to get formatted error details for "Copy Error Details" button
export function getErrorDetails(message: string, context?: Record<string, unknown>, error?: Error): string {
  return JSON.stringify({
    message,
    context,
    error: error ? {
      message: error.message,
      stack: error.stack,
    } : undefined,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  }, null, 2);
}
